import React, { useContext, useEffect, useState } from 'react';
import { NetworkConfig, LoyaltyMember } from '@teko/loyalty';
import { useLoading } from '@teko/ds-desktop';
import { useAuth } from '@teko/auth-react';
import { logger } from '@teko/log';
import { useServices } from './ServiceContext';

interface LoyaltyProviderInterface {
  children: React.ReactNode;
}

interface LoyaltyContextInterface {
  loyaltyMemberInfo?: LoyaltyMember;
  networkConfig?: NetworkConfig;
  loading: boolean;
}

const LoyaltyContext = React.createContext<LoyaltyContextInterface | undefined>(
  undefined,
);

export const useLoyalty = () => {
  const context = useContext(LoyaltyContext);
  if (context === undefined) {
    throw new Error('useLoyalty must be used within a LoyaltyProvider');
  }
  return context;
};

export const LoyaltyProvider: React.FC<LoyaltyProviderInterface> = ({
  children,
}) => {
  const [loyaltyMemberInfo, setLoyaltyMemberInfo] = useState<LoyaltyMember>();
  const [networkConfig, setNetworkConfig] = useState<NetworkConfig>();
  const [loading, setLoading] = useLoading('useLoyalty');
  const { user } = useAuth();
  const { loyaltyBFFService } = useServices();

  useEffect(() => {
    if (user?.userId) {
      const getLoyaltyInfo = async () => {
        try {
          setLoading(true);
          const loyaltyClientId = loyaltyBFFService.getClientId();
          if (loyaltyClientId) {
            const data = await loyaltyBFFService.getLoyaltyMemberInfo();
            setLoyaltyMemberInfo(data);
            setNetworkConfig(await loyaltyBFFService.getNetworkConfig());
          }
        } catch (e) {
          logger.error(e);
        } finally {
          setLoading(false);
        }
      };
      getLoyaltyInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  return (
    <LoyaltyContext.Provider
      value={{
        loyaltyMemberInfo,
        networkConfig,
        loading,
      }}
    >
      {children}
    </LoyaltyContext.Provider>
  );
};
